'use client';

/**
 * ComposerVendorThread — per-vendor quick chat (rail-scope).
 *
 * v6 doctrine: the rail does small + recent; Chat does big +
 * historical. Two specialisations, not one tool doing both jobs.
 *
 * - Last 3 messages clipped. Older history surfaces as a "+ N
 *   earlier · open in Chat →" escape link routing to the dedicated
 *   chat surface with this vendor pre-selected. The rail stays
 *   focused on quick exchanges (2–5 messages of clarification
 *   before commit).
 * - Channel chip next to Send shows the current resolved channel
 *   (icon + short label + caret). Default-resolved per the
 *   precedence table: WhatsApp (phone present) → Note (chatter on
 *   res.partner) — extends to WeChat / Email when those land. Tap
 *   to open a popover with the alternatives. New channels grow the
 *   popover, not the chrome.
 * - Send button is plain "Send", colour-tinted to match the active
 *   channel (green for WhatsApp, primary for Note).
 * - Sends via the same mutation hooks the chat surface uses
 *   (`useSendWhatsApp` + `usePostToChatter`); inbound replies arrive
 *   via the existing Twilio webhook → Prefect → Odoo chatter
 *   pipeline.
 * - Manual phone input only renders when WhatsApp is the active
 *   channel and the vendor record has none. Session-local
 *   `useState` (matches `WhatsAppComposeWithContactSelector`).
 *
 * @see /home/ajs/projects/AIOrchestration/PropertyPortal/erpSetup/v2/klyde_edge/ipe-wireframe/index.html — v6 concept
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain procurement
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2,
  Send,
  AlertCircle,
  MessageSquare,
  Phone,
  MessageSquarePlus,
  ChevronDown,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCartStore } from '@/lib/stores/cart-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useStrategyData, useSendWhatsApp, usePostToChatter } from '@/lib/api/hooks';
import { useComposerLabel, getComposerLabel } from '@/lib/stores/total-matrix-store';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

const VISIBLE_MESSAGE_LIMIT = 3;

type Channel = 'whatsapp' | 'chatter';

interface ChannelDef {
  key: Channel;
  /** i18n label key — resolves via `useComposerLabel(labelKey)`. */
  labelKey: string;
  icon: typeof Phone;
  /** Tailwind classes for the chip background + text. */
  chipClass: string;
  /** Inline style for the active Send button (channel-tinted). */
  sendBg: string;
}

const CHANNELS: Record<Channel, ChannelDef> = {
  whatsapp: {
    key: 'whatsapp',
    labelKey: 'channel_whatsapp',
    icon: Phone,
    chipClass: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30',
    sendBg: '#16a34a',
  },
  chatter: {
    key: 'chatter',
    labelKey: 'channel_note',
    icon: MessageSquarePlus,
    chipClass: 'bg-primary/10 text-primary border-primary/30',
    sendBg: 'var(--primary, #2563eb)',
  },
};

/**
 * Resolve the default channel + which channels are available for a
 * given vendor. Precedence: WhatsApp > Note. Note is the universal
 * fallback (works whenever the vendor has an Odoo res.partner).
 *
 * Future: WeChat (after `id` field), Email (after send_email strategy).
 * Extension shape: append entries to `available` and `precedence`;
 * the chip popover lists whatever's available without UI changes.
 */
function resolveChannel(args: {
  phone: string | null;
  manualPhone: string;
  odooId: number | null;
}): { active: Channel; available: Channel[] } {
  const available: Channel[] = [];
  if (args.phone || args.manualPhone.trim()) available.push('whatsapp');
  if (args.odooId != null) available.push('chatter');
  // Even with no odooId we still surface chatter so the chip is never
  // empty; sendability is gated separately by `canSend`.
  if (available.length === 0) available.push('chatter');
  return { active: available[0], available };
}

interface RawMessage {
  cdk?: string;
  body?: string;
  body_translated?: string | null;
  translated_to?: string | null;
  language?: string | null;
  author_name?: string;
  author_semantic_id?: string;
  subject?: string;
  strategy?: string;
  platform?: string;
  created_date?: string | number;
  created_at?: string | number;
  /**
   * Direction-resolved at the BFF: true = caller authored this message.
   * Computed against `caller_actor_sid` on the JWT, so display strings
   * never enter the comparison. Stamped by the canonical `chat`
   * handler (and previously by `vendor_chat` before this surface
   * migrated). Falls back to `false` = inbound when absent.
   */
  outbound?: boolean;
}

/**
 * Response shape from the unified `chat` strategy
 * (`(messaging, history)` capability). Returns flat `messages` plus
 * compose-context fields the per-vendor rail thread mostly ignores —
 * the bigger composer (chat surface) uses the rest.
 */
interface ChatData {
  messages?: RawMessage[];
  message_count?: number;
  entity_name?: string | null;
  odoo_id?: number | null;
  context_model?: string | null;
}

interface FlatMessage {
  id: string;
  body: string;
  authorName: string;
  createdAt: string | number;
  mine: boolean;
}

interface Props {
  vendorId: string;
  vendorName: string;
  vendorPhone: string | null;
  vendorOdooId: number | null;
  itemCount: number;
  projectName: string | null;
}

export function ComposerVendorThread({
  vendorId,
  vendorName,
  vendorPhone,
  vendorOdooId,
  itemCount,
  projectName,
}: Props) {
  // i18n labels — server-driven, lang follows the matrix fetch.
  const labelPreSubmit = useComposerLabel('thread_pre_submit_label');
  const labelLoading = useComposerLabel('thread_loading');
  const labelLoadFailed = useComposerLabel('thread_load_failed');
  const labelNoMessages = useComposerLabel('thread_no_messages');
  const labelManualPhonePh = useComposerLabel('thread_manual_phone_placeholder');
  const labelManualPhoneClear = useComposerLabel('thread_manual_phone_clear');
  const labelChangeChannel = useComposerLabel('thread_change_channel');
  const labelPopoverNoPhone = useComposerLabel('thread_popover_no_phone');
  const labelDash = useComposerLabel('vendor_dash');
  const labelSend = useComposerLabel('thread_send');
  const labelSending = useComposerLabel('thread_sending');
  const labelNoPhoneYet = useComposerLabel('thread_no_phone_yet');
  const labelFooterWhatsApp = useComposerLabel('thread_footer_whatsapp');
  const labelFooterChatter = useComposerLabel('thread_footer_chatter');
  const labelLines = useComposerLabel(itemCount === 1 ? 'thread_lines_one' : 'thread_lines', { count: itemCount });
  const labelReplyPlaceholder = useComposerLabel('thread_reply_placeholder', { name: vendorName });
  const labelPostsToChatter = useComposerLabel('thread_posts_to_chatter', { name: vendorName });
  const labelChannelWhatsApp = useComposerLabel('channel_whatsapp');
  const labelChannelNote = useComposerLabel('channel_note');

  const draft = useCartStore((s) => s.composerDrafts[vendorId] ?? '');
  const setDraft = useCartStore((s) => s.setComposerDraft);
  const clearDraft = useCartStore((s) => s.clearComposerDraft);

  // Chat read — capability-resolved via (messaging, history). Resolves
  // to the unified `chat` handler which supports `entity_type='vendor'`
  // (returns all messages addressed to this vendor's res.partner across
  // all RFQs). Same canonical translation + direction-stamping pipeline
  // the full Chat surface uses; the rail just clips to the last 3.
  const chatHistoryStrategy = useCapabilityStore((s) =>
    s.strategyName('messaging', 'history'),
  );
  const sendWhatsAppStrategy = useCapabilityStore((s) =>
    s.strategyName('messaging', 'send_whatsapp'),
  );
  const chatterPostStrategy = useCapabilityStore((s) =>
    s.strategyName('messaging', 'chatter_post'),
  );
  const sendWhatsAppMut = useSendWhatsApp(sendWhatsAppStrategy ?? '');
  const postChatterMut = usePostToChatter(chatterPostStrategy ?? '');

  // Viewer language — drives Layer 2 translation in the BFF response.
  const lang = useLanguageStore((s) => s.lang);

  // Escape-link wiring: select the vendor as the active rail entity,
  // then open the chat surface. The chat surface reads selection from
  // the graph-selection-store + viewport-selection store.
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setChatDrawerOpen = useLayoutStore((s) => s.setChatDrawerOpen);

  const { data, isLoading, error, refetch } = useStrategyData<ChatData>(
    chatHistoryStrategy ?? '',
    {
      entity_type: 'vendor',
      entity_sid: vendorId,
      ...(lang ? { lang } : {}),
    },
    !!chatHistoryStrategy,
  );

  // Manual phone — local useState, no persistence. Matches the chat
  // surface's WhatsAppComposeWithContactSelector pattern.
  const [manualPhone, setManualPhone] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);

  // Channel resolution. Default-resolved via the precedence table;
  // user override takes priority while it's set.
  const resolution = useMemo(
    () =>
      resolveChannel({
        phone: vendorPhone,
        manualPhone,
        odooId: vendorOdooId,
      }),
    [vendorPhone, manualPhone, vendorOdooId],
  );
  const [channelOverride, setChannelOverride] = useState<Channel | null>(null);
  const channel: Channel = channelOverride ?? resolution.active;
  const channelDef = CHANNELS[channel];

  // Channel popover open state + click-outside close.
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!popoverOpen) return;
    const handler = (e: MouseEvent) => {
      if (!popoverRef.current) return;
      if (!popoverRef.current.contains(e.target as Node)) setPopoverOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popoverOpen]);

  const allMessages = useMemo<FlatMessage[]>(() => {
    const all: FlatMessage[] = [];
    const messages = data?.data?.messages ?? [];
    for (const m of messages) {
      // Prefer the translated body when the BFF returns one (Layer 2
      // of docs/i18n_concept.md). Falls back to the original `body`
      // — same behaviour as the chat surface.
      const raw = m.body_translated ?? m.body ?? '';
      const body = raw.trim();
      if (!body) continue;
      all.push({
        id: m.cdk ?? `vendor-${vendorId}-${all.length}`,
        body,
        authorName: m.author_name ?? '—',
        createdAt: m.created_date ?? m.created_at ?? 0,
        // BFF-stamped direction. True = caller authored. No
        // consumer-side heuristic, no string matching against
        // display names — the BFF compares author_semantic_id
        // to the JWT's caller_actor_sid at read time.
        mine: m.outbound === true,
      });
    }
    all.sort((a, b) => compareTimestamps(a.createdAt, b.createdAt));
    return all;
  }, [data, vendorId]);

  const visibleMessages = allMessages.slice(-VISIBLE_MESSAGE_LIMIT);
  const earlierCount = allMessages.length - visibleMessages.length;

  const effectivePhone = vendorPhone || manualPhone.trim() || null;
  const isSending = sendWhatsAppMut.isPending || postChatterMut.isPending;

  const canSend = (() => {
    if (!draft.trim() || isSending || vendorOdooId == null) return false;
    if (channel === 'whatsapp') return !!effectivePhone && !!sendWhatsAppStrategy;
    return !!chatterPostStrategy;
  })();

  const handleSend = async () => {
    if (!canSend || vendorOdooId == null) return;
    setSendError(null);

    try {
      if (channel === 'whatsapp') {
        if (!effectivePhone) return;
        const result = await sendWhatsAppMut.mutateAsync({
          to: effectivePhone,
          message: draft,
          context_model: 'res.partner',
          context_id: vendorOdooId,
        });
        if (!result.whatsapp_sent) {
          setSendError(result.whatsapp_error || getComposerLabel('thread_whatsapp_send_failed'));
          return;
        }
        if (result.chatter_posted) {
          toast.success(getComposerLabel('thread_whatsapp_sent_logged', { name: vendorName }));
        } else {
          toast.warning(
            result.chatter_error
              ? getComposerLabel('thread_whatsapp_chatter_failed_err', { name: vendorName, err: result.chatter_error })
              : getComposerLabel('thread_whatsapp_chatter_failed', { name: vendorName }),
          );
        }
      } else {
        const result = await postChatterMut.mutateAsync({
          message: draft,
          context_model: 'res.partner',
          context_id: vendorOdooId,
        });
        if (!result.success) {
          setSendError(result.error || getComposerLabel('thread_chatter_post_failed'));
          return;
        }
        toast.success(getComposerLabel('thread_note_posted', { name: vendorName }));
      }
      clearDraft(vendorId);
      await refetch();
    } catch (e) {
      setSendError(e instanceof Error ? e.message : getComposerLabel('thread_send_failed_generic'));
    }
  };

  const handleEscapeToChat = () => {
    selectNode({
      id: vendorId,
      label: vendorName,
      type: 'vendor',
      properties: { phone: vendorPhone, odoo_id: vendorOdooId },
    });
    setChatDrawerOpen(true);
  };

  const ChannelIcon = channelDef.icon;

  return (
    <div className="space-y-2 px-3 pb-3">
      {/* Context strip — what this thread is about */}
      <div
        className={cn(
          'rounded-md border border-border bg-background px-2.5 py-1.5 flex items-center gap-2',
        )}
      >
        <span
          className={cn(
            text.muted,
            'uppercase tracking-wider font-semibold text-muted-foreground',
          )}
        >
          {labelPreSubmit}
        </span>
        <span className={cn(text.muted, 'truncate text-muted-foreground')}>
          {labelLines}
          {projectName ? ` · ${projectName}` : ''}
        </span>
      </div>

      {/* Escape link — older history routes to the dedicated Chat surface */}
      {earlierCount > 0 && (
        <EscapeLinkButton
          earlierCount={earlierCount}
          onClick={handleEscapeToChat}
          className={cn(text.muted, 'flex-1 text-muted-foreground')}
        />
      )}

      {/* Messages — last 3 only */}
      <div className="rounded-md border border-border bg-background">
        {isLoading ? (
          <div className={cn(text.muted, 'flex items-center gap-1.5 px-2.5 py-3 text-muted-foreground')}>
            <Loader2 className="h-3 w-3 animate-spin" />
            {labelLoading}
          </div>
        ) : error ? (
          <div className={cn(text.muted, 'px-2.5 py-3 text-destructive')}>
            {labelLoadFailed}
          </div>
        ) : visibleMessages.length === 0 ? (
          <div className="flex flex-col items-center gap-1 px-2.5 py-4 text-muted-foreground">
            <MessageSquare className="h-4 w-4 opacity-50" />
            <p className={cn(text.muted, 'text-muted-foreground')}>{labelNoMessages}</p>
          </div>
        ) : (
          <div className="space-y-1.5 p-2">
            {visibleMessages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
          </div>
        )}
      </div>

      {/* Manual phone — render whenever the vendor record has no
          phone, regardless of the active channel. Typing a number
          enables WhatsApp in the chip popover (resolution.available
          recomputes when manualPhone changes). Without this, the
          buyer is stuck on Note (the fallback) with no path to
          WhatsApp — Catch-22: WhatsApp needs a phone, the input was
          gated on WhatsApp being selected. */}
      {!vendorPhone && (
        <div className="flex items-center gap-1.5">
          <Phone className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          <input
            type="tel"
            value={manualPhone}
            onChange={(e) => setManualPhone(e.target.value)}
            placeholder={labelManualPhonePh}
            className={cn(
              text.body,
              'flex-1 px-2 py-1 rounded-md border border-border bg-background',
              'placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary',
            )}
          />
          {manualPhone && (
            <button
              type="button"
              onClick={() => setManualPhone('')}
              className={cn(text.muted, 'underline hover:no-underline text-muted-foreground')}
            >
              {labelManualPhoneClear}
            </button>
          )}
        </div>
      )}

      {/* Composer — single textarea, channel chip + Send */}
      <div className="rounded-md border border-border bg-background">
        <textarea
          value={draft}
          onChange={(e) => setDraft(vendorId, e.target.value)}
          placeholder={labelReplyPlaceholder}
          rows={1}
          className={cn(
            text.body,
            'w-full px-2.5 py-1.5 bg-transparent border-0 outline-none resize-none',
            'placeholder:text-muted-foreground/60',
            'min-h-[36px]',
          )}
        />
        <div className="flex items-center justify-between gap-2 px-2 py-1 border-t border-border/60 bg-card-muted">
          <ComposerToMeta
            channel={channel}
            effectivePhone={effectivePhone}
            postsToChatterLabel={labelPostsToChatter}
            noPhoneYetLabel={labelNoPhoneYet}
            className={cn(text.muted, 'truncate text-muted-foreground')}
          />

          {/* Channel chip — current channel + caret. Tap to open
              popover with available alternatives. */}
          <div className="relative" ref={popoverRef}>
            <button
              type="button"
              onClick={() => setPopoverOpen((v) => !v)}
              title={labelChangeChannel}
              className={cn(
                text.muted,
                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-semibold text-muted-foreground',
                channelDef.chipClass,
              )}
            >
              <ChannelIcon className="h-3 w-3" />
              {channel === 'whatsapp' ? labelChannelWhatsApp : labelChannelNote}
              <ChevronDown className="h-3 w-3 opacity-70" />
            </button>
            {popoverOpen && (
              <div
                className={cn(
                  'absolute right-0 bottom-full mb-1 z-50 min-w-[140px]',
                  'rounded-md border border-border bg-popover shadow-lg p-1',
                )}
              >
                {(Object.keys(CHANNELS) as Channel[]).map((key) => {
                  const def = CHANNELS[key];
                  const Icon = def.icon;
                  const isActive = channel === key;
                  const isAvailable = resolution.available.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setChannelOverride(key);
                        setPopoverOpen(false);
                      }}
                      disabled={!isAvailable && key === 'whatsapp' && !manualPhone}
                      className={cn(
                        text.body,
                        'w-full flex items-center gap-2 px-2 py-1.5 rounded text-left',
                        isActive ? 'bg-muted' : 'hover:bg-muted',
                        !isAvailable && 'opacity-60',
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span className="flex-1">
                        {key === 'whatsapp' ? labelChannelWhatsApp : labelChannelNote}
                      </span>
                      {!isAvailable && (
                        <span className={cn(text.muted, 'text-[9px] text-muted-foreground')}>
                          {key === 'whatsapp' ? labelPopoverNoPhone : labelDash}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            onClick={handleSend}
            disabled={!canSend}
            style={canSend ? { backgroundColor: channelDef.sendBg } : undefined}
            className={cn(
              text.body,
              'inline-flex items-center gap-1 px-2.5 py-1 rounded font-medium text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              !canSend && 'bg-muted-foreground',
            )}
          >
            {isSending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                {labelSending}
              </>
            ) : (
              <>
                <Send className="h-3 w-3" />
                {labelSend}
              </>
            )}
          </button>
        </div>
      </div>

      {sendError && (
        <div className="rounded-md p-2 flex items-start gap-2 bg-destructive/10 text-destructive">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <p className={text.body}>{sendError}</p>
        </div>
      )}

      <p className={cn(text.muted, 'italic text-muted-foreground')}>
        {channel === 'whatsapp' ? labelFooterWhatsApp : labelFooterChatter}
      </p>
    </div>
  );
}

/**
 * "to {phone}" / "No phone yet" / "Posts to {name}'s chatter" — the
 * one piece of channel-conditional metadata next to the Send button.
 * Pulled into a helper so the labels are looked up unconditionally
 * at render (hooks-rules safety) but rendered with the right value.
 */
function ComposerToMeta({
  channel,
  effectivePhone,
  postsToChatterLabel,
  noPhoneYetLabel,
  className,
}: {
  channel: Channel;
  effectivePhone: string | null;
  postsToChatterLabel: string;
  noPhoneYetLabel: string;
  className?: string;
}) {
  const toPhoneLabel = useComposerLabel('thread_to_phone', { phone: effectivePhone ?? '' });
  if (channel === 'whatsapp') {
    return <span className={className}>{effectivePhone ? toPhoneLabel : noPhoneYetLabel}</span>;
  }
  return <span className={className}>{postsToChatterLabel}</span>;
}

/**
 * Escape link routing the buyer to the dedicated Chat surface for
 * the long-tail history. Pulled out so the {count}-templated label
 * is looked up unconditionally even when earlierCount === 0
 * (parent gate decides whether the button renders at all).
 */
function EscapeLinkButton({
  earlierCount,
  onClick,
  className,
}: {
  earlierCount: number;
  onClick: () => void;
  className?: string;
}) {
  const label = useComposerLabel(
    earlierCount === 1 ? 'thread_earlier_one' : 'thread_earlier',
    { count: earlierCount },
  );
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md w-full text-left',
        'bg-card-muted border border-dashed border-border hover:border-primary/40 hover:bg-muted transition-colors',
      )}
    >
      <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
      <span className={className}>{label}</span>
    </button>
  );
}

function MessageBubble({ message }: { message: FlatMessage }) {  return (
    <div className={cn('flex', message.mine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[78%] rounded-lg px-2.5 py-1.5',
          message.mine
            ? 'bg-primary/10 border border-primary/25 text-primary'
            : 'bg-card border border-border',
        )}
      >
        {!message.mine && (
          <div className={cn(text.muted, 'mb-0.5 font-medium text-muted-foreground')}>{message.authorName}</div>
        )}
        <div className={text.body}>{message.body}</div>
        <div className={cn(text.muted, 'mt-1 opacity-70 text-muted-foreground')}>{formatTime(message.createdAt)}</div>
      </div>
    </div>
  );
}

function compareTimestamps(a: string | number, b: string | number): number {
  const av = typeof a === 'number' ? a : Date.parse(String(a)) || 0;
  const bv = typeof b === 'number' ? b : Date.parse(String(b)) || 0;
  return av - bv;
}

function formatTime(ts: string | number): string {
  const ms = typeof ts === 'number' ? ts : Date.parse(String(ts));
  if (!ms || Number.isNaN(ms)) return '';
  const d = new Date(ms);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return time;
  const datePart = d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  return `${datePart} · ${time}`;
}
